import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface WishlistItem {
  itemId: string;
  title: string;
  image: string;
  pricePerDay: number;
  city: string;
  ownerName: string;
  addedAt: Date;
}

interface WishlistContextType {
  items: WishlistItem[];
  addToWishlist: (item: Omit<WishlistItem, 'addedAt'>) => void;
  removeFromWishlist: (itemId: string) => void;
  isInWishlist: (itemId: string) => boolean;
  clearWishlist: () => void;
  getWishlistCount: () => number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<WishlistItem[]>(() => {
    const saved = localStorage.getItem('rentify-wishlist');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((item: any) => ({
        ...item,
        addedAt: new Date(item.addedAt),
      }));
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('rentify-wishlist', JSON.stringify(items));
  }, [items]);

  const addToWishlist = (item: Omit<WishlistItem, 'addedAt'>) => {
    if (isInWishlist(item.itemId)) return;
    
    const newItem: WishlistItem = {
      ...item,
      addedAt: new Date(),
    };
    setItems((prev) => [...prev, newItem]);
  };

  const removeFromWishlist = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.itemId !== itemId));
  };

  const isInWishlist = (itemId: string) => items.some((item) => item.itemId === itemId);

  const clearWishlist = () => {
    setItems([]);
  };

  const getWishlistCount = () => items.length;

  return (
    <WishlistContext.Provider
      value={{
        items,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        getWishlistCount,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
